import api from './api.service';
import type { RejectionPayload } from './rejection.service';

/** Recognition Engine (Prepaid Expense Management) API client — `/finance/recognition/*`. Amounts are exact decimal strings. */

export const CONTRACT_STATUSES = [
  'DRAFT',
  'PENDING_APPROVAL',
  'APPROVED',
  'SCHEDULED',
  'ACTIVE',
  'SUSPENDED',
  'TERMINATING',
  'TERMINATED',
  'FULLY_RECOGNIZED',
  'CLOSED',
  'CANCELLED',
] as const;
export type ContractStatus = (typeof CONTRACT_STATUSES)[number];

export const LINE_STATUSES = [
  'PLANNED',
  'DUE',
  'DRAFT_JV_CREATED',
  'POSTED',
  'PARTIALLY_POSTED',
  'HELD',
  'SKIPPED',
  'REVERSED',
  'CANCELLED',
  'POSTING_BLOCKED',
] as const;

export const RECOGNITION_METHODS = ['DAILY_PRO_RATA', 'STRAIGHT_LINE_MONTHLY', 'EQUAL_ACCOUNTING_PERIOD', 'CUSTOM_SCHEDULE'] as const;
export const DAY_COUNT_METHODS = ['ACTUAL_CALENDAR_DAYS', 'ACTUAL_365', 'ACTUAL_366', 'THIRTY_DAY_MONTH', 'ACCOUNTING_PERIOD_DAYS'] as const;
export const PARTIAL_MONTH_POLICIES = ['PRO_RATA', 'FULL_MONTH'] as const;
export const POSTING_MODES = ['MANUAL', 'AUTO_CREATE_DRAFT', 'AUTO_POST'] as const;
export const POSTING_GRANULARITIES = [
  'ONE_JV_PER_CONTRACT',
  'CONSOLIDATED_BY_EXPENSE_ACCOUNT',
  'CONSOLIDATED_BY_SUPPLIER',
  'CONSOLIDATED_BY_RECOGNITION_RUN',
] as const;
export const INITIAL_POSTING_MODES = ['NONE', 'POST_ON_ACTIVATION'] as const;
export const REVISION_TYPES = [
  'REVISION',
  'EXTENSION',
  'ADDITION',
  'CHANGE_GL',
  'CHANGE_DIMENSIONS',
  'SUSPENSION',
  'RESUME',
  'TERMINATION',
  'REFUND',
  'WRITE_OFF',
  'REVERSAL',
] as const;
export type RevisionType = (typeof REVISION_TYPES)[number];
export const SUSPENSION_TREATMENTS = ['EXTEND_SERVICE_END', 'REDISTRIBUTE_REMAINING', 'HOLD_RECOGNITION'] as const;
export const ADDITION_TREATMENTS = ['SPREAD_OVER_REMAINING_PERIOD', 'SEPARATE_SCHEDULE'] as const;
export const CLOSED_PERIOD_POLICIES = ['HOLD', 'NEXT_OPEN_PERIOD'] as const;
export const BLOCKED_LINE_ACTIONS = ['HOLD', 'RELEASE', 'POST_TO_NEXT_OPEN_PERIOD', 'CURRENT_PERIOD_ADJUSTMENT', 'REQUEST_REOPENING'] as const;
export const APPROVAL_EVENTS = [
  'CREATE',
  'ACTIVATE',
  'CUSTOM_SCHEDULE',
  'REVISE',
  'CHANGE_GL',
  'CHANGE_SERVICE_DATES',
  'SUSPEND',
  'RESUME',
  'TERMINATE',
  'REFUND',
  'WRITE_OFF',
  'REVERSE',
  'MANUAL_RECOGNITION',
] as const;
export const EXPENSE_TREATMENTS = ['NORMAL_EXPENSE', 'PREPAID_EXPENSE'] as const;

/** Adjustment type → permission code (mirrors backend REVISION_PERMISSION). */
export const REVISION_PERMISSION: Record<RevisionType, string> = {
  REVISION: 'finance.recognition.revise',
  EXTENSION: 'finance.recognition.revise',
  ADDITION: 'finance.recognition.revise',
  CHANGE_GL: 'finance.recognition.revise',
  CHANGE_DIMENSIONS: 'finance.recognition.revise',
  SUSPENSION: 'finance.recognition.suspend',
  RESUME: 'finance.recognition.resume',
  TERMINATION: 'finance.recognition.terminate',
  REFUND: 'finance.recognition.refund',
  WRITE_OFF: 'finance.recognition.writeoff',
  REVERSAL: 'finance.recognition.reverse',
};

const ADJUSTABLE: ContractStatus[] = ['SCHEDULED', 'ACTIVE', 'SUSPENDED'];
/** Contract statuses accepting each adjustment type (mirrors backend ALLOWED_STATUSES). */
export const ADJUSTMENT_ALLOWED_STATUSES: Record<RevisionType, ContractStatus[]> = {
  REVISION: ADJUSTABLE,
  EXTENSION: [...ADJUSTABLE, 'FULLY_RECOGNIZED'],
  ADDITION: ADJUSTABLE,
  CHANGE_GL: ADJUSTABLE,
  CHANGE_DIMENSIONS: ADJUSTABLE,
  SUSPENSION: ['SCHEDULED', 'ACTIVE'],
  RESUME: ['SUSPENDED'],
  TERMINATION: ADJUSTABLE,
  REFUND: ADJUSTABLE,
  WRITE_OFF: ADJUSTABLE,
  REVERSAL: [...ADJUSTABLE, 'FULLY_RECOGNIZED'],
};

export const RECOGNITION_PERMISSIONS = {
  VIEW: 'finance.recognition.view',
  CREATE: 'finance.recognition.create',
  EDIT: 'finance.recognition.edit',
  SUBMIT: 'finance.recognition.submit',
  APPROVE: 'finance.recognition.approve',
  ACTIVATE: 'finance.recognition.activate',
  RUN: 'finance.recognition.run',
  POST: 'finance.recognition.post',
  RECONCILE: 'finance.recognition.reconcile',
  ADMIN: 'finance.recognition.admin',
} as const;

export type AllocationInput = {
  allocation_basis: 'PERCENTAGE' | 'FIXED_AMOUNT';
  percentage?: string | number | null;
  fixed_amount?: string | number | null;
  expense_account_id?: string | null;
  cost_center?: string | null;
  project_id?: string | null;
  department_id?: string | null;
  branch_id?: string | null;
  effective_from?: string | null;
  effective_to?: string | null;
};

export type CustomScheduleLineInput = { from: string; to: string; amount: string | number };

export type ContractInput = {
  category_id?: string | null;
  description?: string;
  notes?: string | null;
  supplier_id?: string | null;
  supplier_invoice_number?: string | null;
  invoice_date?: string | null;
  purchase_order_reference?: string | null;
  contract_reference?: string | null;
  branch_id?: string | null;
  department_id?: string | null;
  cost_center?: string | null;
  project_id?: string | null;
  transaction_currency?: string | null;
  exchange_rate?: string | number | null;
  transaction_amount?: string | number;
  recognizable_amount?: string | number | null;
  service_start_date?: string;
  service_end_date?: string;
  recognition_start_date?: string | null;
  recognition_end_date?: string | null;
  recognition_method?: string;
  day_count_method?: string;
  partial_month_policy?: string;
  posting_mode?: string;
  posting_granularity?: string | null;
  prepaid_asset_account_id?: string | null;
  expense_account_id?: string | null;
  initial_posting_mode?: string;
  source_clearing_account_id?: string | null;
  initial_recognized_on?: string | null;
  allocations?: AllocationInput[];
  custom_lines?: CustomScheduleLineInput[];
  opening?: {
    original_amount: string | number;
    historically_recognized_amount: string | number;
    migration_date: string;
    original_service_start: string;
    original_service_end: string;
  } | null;
  version?: number;
};

export type ContractListRow = {
  id: string;
  recognition_number: string;
  status: ContractStatus;
  source_type: string;
  source_document_number: string | null;
  description: string;
  supplier_id: string | null;
  supplier_name: string | null;
  category_id: string | null;
  category_name: string | null;
  transaction_currency: string;
  base_currency: string;
  transaction_amount: string;
  recognizable_amount: string;
  recognizable_base_amount: string;
  recognized_amount: string;
  recognized_base_amount: string;
  remaining_amount: string;
  remaining_base_amount: string;
  service_start_date: string;
  service_end_date: string;
  recognition_start_date: string;
  recognition_end_date: string;
  recognition_method: string;
  posting_mode: string;
  prepaid_asset_account_id: string;
  prepaid_account_code: string | null;
  prepaid_account_name: string | null;
  expense_account_id: string;
  expense_account_code: string | null;
  expense_account_name: string | null;
  project_id: string | null;
  cost_center: string | null;
  created_at: string;
};

export type Paged<T> = { data: T[]; total: number; page: number; limit: number };

export type Allocation = AllocationInput & { id: string; line_no: number };

export type ContractDetail = {
  id: string;
  company_id: string;
  recognition_number: string;
  recognition_type: string;
  status: ContractStatus;
  current_revision: number;
  version: number;
  source_type: string;
  source_module: string | null;
  source_document_type: string | null;
  source_document_id: string | null;
  source_document_line_id: string | null;
  source_document_number: string | null;
  parent_contract_id: string | null;
  parent_recognition_number: string | null;
  supplier_id: string | null;
  supplier_name: string | null;
  supplier_code: string | null;
  supplier_invoice_number: string | null;
  invoice_date: string | null;
  purchase_order_reference: string | null;
  contract_reference: string | null;
  description: string;
  notes: string | null;
  category_id: string | null;
  category_name: string | null;
  category_code: string | null;
  legal_entity_id: string | null;
  branch_id: string | null;
  business_unit_id: string | null;
  department_id: string | null;
  department_code: string | null;
  department_name: string | null;
  branch_code: string | null;
  branch_name: string | null;
  cost_center: string | null;
  project_id: string | null;
  project_name: string | null;
  transaction_currency: string;
  base_currency: string;
  exchange_rate: string;
  transaction_amount: string;
  base_amount: string;
  recognizable_amount: string;
  recognizable_base_amount: string;
  recognized_amount: string;
  recognized_base_amount: string;
  remaining_amount: string;
  remaining_base_amount: string;
  adjustment_amount: string;
  refunded_amount: string;
  written_off_amount: string;
  terminated_expense_amount: string;
  original_amount: string | null;
  historically_recognized_amount: string | null;
  opening_remaining_amount: string | null;
  migration_adjustment_amount: string | null;
  original_service_start: string | null;
  original_service_end: string | null;
  migration_date: string | null;
  service_start_date: string;
  service_end_date: string;
  recognition_start_date: string;
  recognition_end_date: string;
  recognition_method: string;
  day_count_method: string;
  partial_month_policy: string;
  posting_mode: string;
  posting_granularity: string | null;
  prepaid_asset_account_id: string;
  prepaid_account_code: string | null;
  prepaid_account_name: string | null;
  prepaid_account_controlled: number | boolean | null;
  expense_account_id: string;
  expense_account_code: string | null;
  expense_account_name: string | null;
  initial_posting_mode: string;
  source_clearing_account_id: string | null;
  clearing_account_code: string | null;
  clearing_account_name: string | null;
  initial_journal_voucher_id: string | null;
  initial_journal_voucher_number: string | null;
  initial_recognized_on: string | null;
  suspension_start_date: string | null;
  suspension_end_date: string | null;
  suspension_treatment: string | null;
  suspension_reason: string | null;
  termination_date: string | null;
  approval_instance_id: string | null;
  submitted_at: string | null;
  approved_at: string | null;
  activated_at: string | null;
  fully_recognized_at: string | null;
  closed_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
  allocations: Allocation[];
  line_status_summary: Array<{ status: string; n: number | string; amount: string }>;
  next_line: { id: string; accounting_period_key: string; posting_date: string; planned_amount: string; status: string } | null;
  child_contracts: Array<{ id: string; recognition_number: string; status: string; recognizable_amount: string }>;
  pending_revision_count: number;
};

export type ScheduleLineRow = {
  id: string;
  revision_no: number;
  sequence_no: number;
  accounting_period_key: string;
  fiscal_year_name: string | null;
  recognition_from_date: string;
  recognition_to_date: string;
  service_days: number;
  recognition_days: number;
  planned_amount: string;
  posted_amount: string;
  remaining_amount: string;
  planned_base_amount: string;
  posted_base_amount: string;
  posting_date: string;
  posting_date_override: string | null;
  status: string;
  is_manual: number | boolean;
  exception_code: string | null;
  exception_message: string | null;
  recognition_posting_id: string | null;
  journal_voucher_id: string | null;
  journal_voucher_number: string | null;
  reversal_journal_voucher_id: string | null;
  reversal_journal_voucher_number: string | null;
  re_recognition_of_line_id: string | null;
  recognition_run_id: string | null;
  draft_journal_voucher_id: string | null;
  draft_journal_voucher_number: string | null;
  version: number;
};

export type SchedulePreviewLine = {
  periodKey: string;
  periodStart: string;
  periodEnd: string;
  from: string;
  to: string;
  serviceDays: number;
  recognitionDays: number;
  amount: string;
  baseAmount: string;
  postingDate: string;
  isManual: boolean;
};

export type SchedulePreview = {
  lines: SchedulePreviewLine[];
  totalAmount: string;
  totalBaseAmount: string;
  totalServiceDays: number;
  totalRecognitionDays: number;
  base_currency: string;
  recognizable_base_amount: string;
};

export type ContractEvent = {
  id: string;
  event_type: string;
  event_source: string;
  actor_user_id: string | null;
  actor_name: string | null;
  reason: string | null;
  before_json: unknown;
  after_json: unknown;
  metadata: unknown;
  schedule_line_id: string | null;
  revision_id: string | null;
  recognition_run_id: string | null;
  journal_voucher_id: string | null;
  event_at: string;
};

export type ContractJournal = {
  id: string;
  voucher_number: string;
  voucher_date: string;
  status: string;
  total_debit: string;
  description: string | null;
  source_type: string | null;
  posting_event: string | null;
  role: string;
};

export type ContractMovement = {
  id: string;
  movement_type: string;
  movement_date: string;
  amount: string;
  base_amount: string;
  journal_voucher_id: string | null;
  voucher_number: string | null;
  in_gl: number | boolean;
  description: string | null;
  created_at: string;
};

export type Adjustment = {
  id: string;
  recognition_contract_id: string;
  adjustment_no: number;
  revision_no: number | null;
  revision_type: RevisionType;
  effective_date: string;
  reason: string;
  amount: string;
  payload: Record<string, unknown> | null;
  previous_snapshot: Record<string, unknown> | null;
  new_snapshot: Record<string, unknown> | null;
  remaining_amount_before: string | null;
  remaining_amount_after: string | null;
  status: 'PENDING_APPROVAL' | 'APPLIED' | 'REJECTED' | 'FAILED';
  approval_instance_id: string | null;
  journal_voucher_id: string | null;
  journal_voucher_number: string | null;
  schedule_line_id: string | null;
  child_contract_id: string | null;
  failure_message: string | null;
  rejection_reason: string | null;
  created_by: string | null;
  created_by_name?: string | null;
  approved_by_name?: string | null;
  created_at: string;
  approved_at: string | null;
  applied_at: string | null;
  recognition_number?: string;
  description?: string;
};

export type AdjustmentInput = {
  revision_type: RevisionType;
  effective_date: string;
  reason: string;
  service_end_date?: string | null;
  recognition_end_date?: string | null;
  recognition_method?: string | null;
  day_count_method?: string | null;
  partial_month_policy?: string | null;
  custom_lines?: CustomScheduleLineInput[];
  amount?: string | number | null;
  exchange_rate?: string | number | null;
  addition_treatment?: string | null;
  post_journal?: boolean;
  source_clearing_account_id?: string | null;
  source_document_type?: string | null;
  source_document_id?: string | null;
  source_document_number?: string | null;
  separate_service_start?: string | null;
  separate_service_end?: string | null;
  expense_account_id?: string | null;
  allocations?: AllocationInput[];
  cost_center?: string | null;
  project_id?: string | null;
  department_id?: string | null;
  branch_id?: string | null;
  suspension_treatment?: string | null;
  suspension_end_date?: string | null;
  refund_amount?: string | number | null;
  write_off_amount?: string | number | null;
  refund_account_id?: string | null;
  write_off_account_id?: string | null;
  account_id?: string | null;
  schedule_line_id?: string | null;
  re_recognize?: boolean;
};

export type LineOutcome = {
  lineId: string;
  contractId: string;
  status: 'POSTED' | 'DRAFT_CREATED' | 'EXCEPTION' | 'SKIPPED';
  exceptionCode?: string;
  message?: string;
  postingId?: string;
  journalVoucherId?: string;
  journalVoucherNumber?: string;
  amount: string;
  baseAmount: string;
};

export type RunFilters = {
  through_date: string;
  contract_ids?: string[];
  category_id?: string | null;
  supplier_id?: string | null;
  expense_account_id?: string | null;
  prepaid_asset_account_id?: string | null;
  project_id?: string | null;
  branch_id?: string | null;
};

export type RunCreateInput = RunFilters & { posting_action?: 'POST' | 'DRAFT'; posting_granularity?: string | null };

export type RunPreviewItem = {
  lineId: string;
  contractId: string;
  recognitionNumber: string;
  description: string;
  supplierName: string | null;
  periodKey: string;
  postingDate: string;
  amount: string;
  baseAmount: string;
  status: 'READY' | 'EXCEPTION';
  exceptionCode: string | null;
  exceptionMessage: string | null;
};

export type RunPreview = {
  throughDate: string;
  itemCount: number;
  eligibleAmount: string;
  readyCount: number;
  readyAmount: string;
  exceptionCount: number;
  exceptionAmount: string;
  items: RunPreviewItem[];
  truncated: boolean;
};

export type RecognitionRun = {
  id: string;
  run_number: string;
  run_date: string;
  recognition_through_date: string;
  filters: Record<string, unknown> | null;
  posting_action: 'POST' | 'DRAFT';
  posting_granularity: string;
  status: 'DRAFT' | 'VALIDATED' | 'PROCESSING' | 'PARTIALLY_COMPLETED' | 'COMPLETED' | 'FAILED';
  item_count: number;
  eligible_amount: string;
  ready_amount: string;
  posted_amount: string;
  exception_amount: string;
  exception_count: number;
  triggered_by: string;
  failure_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  created_by_name?: string | null;
};

export type RunItem = {
  id: string;
  recognition_contract_id: string;
  schedule_line_id: string;
  status: string;
  amount: string;
  base_amount: string;
  exception_code: string | null;
  exception_message: string | null;
  journal_voucher_id: string | null;
  journal_voucher_number: string | null;
  recognition_number: string;
  description: string;
  accounting_period_key: string;
  posting_date: string;
  line_status: string;
};

export type RunDetail = { run: RecognitionRun; items: Paged<RunItem>; status_counts: Record<string, number> };

export type RecognitionPolicy = {
  id: string;
  default_posting_mode: string;
  posting_granularity: string;
  closed_period_policy: string;
  allow_auto_post: boolean | number;
  allow_re_recognition: boolean | number;
  refund_receivable_account_id: string | null;
  write_off_account_id: string | null;
  approval_required_events: string[] | null;
  rounding_tolerance: string;
  expiry_notice_days: number;
  updated_at: string;
};

export type RecognitionCategory = {
  id: string;
  code: string;
  name: string;
  default_prepaid_asset_account_id: string | null;
  default_expense_account_id: string | null;
  default_recognition_method: string | null;
  default_posting_mode: string | null;
  approval_policy_id: string | null;
  is_active: boolean | number;
};

export type ControlledAccount = {
  id: string;
  account_code: string;
  account_name: string;
  account_type: string;
  is_group: boolean | number;
  is_active: boolean | number;
  recognition_controlled: boolean | number;
};

export type WorkspaceData = {
  as_of: string;
  kpis: {
    live_contracts: number;
    suspended_contracts: number;
    remaining_base: string;
    recognized_this_month: string;
    remaining_this_month: string;
    due_count: number;
    due_base: string;
    overdue_count: number;
    overdue_base: string;
    blocked_count: number;
    blocked_base: string;
    draft_count: number;
    draft_base: string;
    contracts_pending_approval: number;
    adjustments_pending_approval: number;
    adjustments_failed: number;
    contracts_not_active: number;
    expiring_30_days: number;
    expiring_30_days_base: string;
    failed_runs_30_days: number;
    due_today_count: number;
    due_today_base: string;
    fully_recognized_contracts: number;
  };
  status_distribution: Array<{ status: string; contract_count: number | string; remaining_base: string }>;
  remaining_by_supplier: Array<{ supplier: string; contract_count: number | string; remaining_base: string }>;
  remaining_by_category: Array<{ category: string; contract_count: number | string; remaining_base: string }>;
  expiry_aging: { d0_30: string; d31_90: string; d91_180: string; d181_365: string; over_365: string; past_end: string };
  forecast: { periods: string[]; totals: Record<string, string>; grand_total: string };
  due_lines: Array<{
    id: string;
    recognition_contract_id: string;
    recognition_number: string;
    description: string;
    supplier_name: string | null;
    accounting_period_key: string;
    posting_date: string;
    planned_base_amount: string;
    status: string;
  }>;
  blocked_lines: Array<{
    id: string;
    recognition_contract_id: string;
    recognition_number: string;
    description: string;
    accounting_period_key: string;
    posting_date: string;
    planned_base_amount: string;
    exception_code: string | null;
    exception_message: string | null;
  }>;
  draft_postings: Array<{
    id: string;
    draft_journal_voucher_id: string | null;
    draft_journal_voucher_number: string | null;
    posting_date: string;
    total_base_amount: string;
    line_count: number;
    granularity: string;
    created_at: string;
  }>;
  pending_approvals: Array<{
    kind: 'contract' | 'adjustment';
    id: string;
    contract_id: string;
    number: string;
    label: string | null;
    amount: string;
    requested_at: string;
  }>;
  expiring_contracts: Array<{ id: string; recognition_number: string; description: string; recognition_end_date: string; remaining_base_amount: string }>;
};

export type RegisterReport = Paged<ContractListRow> & {
  totals: { contract_count: number | string; recognizable_base_amount: string; recognized_base_amount: string; remaining_base_amount: string };
};

export type ScheduleReportRow = {
  id: string;
  recognition_contract_id: string;
  recognition_number: string;
  description: string;
  supplier_name: string | null;
  expense_account_code: string | null;
  expense_account_name: string | null;
  sequence_no: number;
  accounting_period_key: string;
  recognition_from_date: string;
  recognition_to_date: string;
  posting_date: string;
  recognition_days: number;
  transaction_currency: string;
  planned_amount: string;
  planned_base_amount: string;
  posted_base_amount: string;
  status: string;
  exception_code: string | null;
  journal_voucher_id: string | null;
  journal_voucher_number: string | null;
};

export type ScheduleReport = Paged<ScheduleReportRow> & {
  by_period: Array<{ accounting_period_key: string; scheduled_base: string; posted_base: string; open_base: string }>;
};

export type MovementAmounts = {
  opening: string;
  additions: string;
  recognized: string;
  refunds: string;
  write_offs: string;
  termination_expense: string;
  source_reversals: string;
  closing: string;
};

export type MovementReport = {
  from_date: string;
  to_date: string;
  group_by: string;
  rows: Array<
    MovementAmounts & {
      key_id: string;
      key_code: string | null;
      key_name: string | null;
      account_code: string | null;
      account_name: string | null;
      supplier_name: string | null;
    }
  >;
  totals: MovementAmounts;
};

export type ForecastReport = {
  as_of: string;
  group_by: string;
  periods: string[];
  rows: Array<{ dim_id: string; label: string; values: Record<string, string>; total: string }>;
  totals: Record<string, string>;
  grand_total: string;
};

export type ReconciliationException = {
  category: string;
  account_id: string;
  amount: string;
  journal_voucher_id: string | null;
  voucher_number: string | null;
  voucher_date: string | null;
  recognition_contract_id: string | null;
  recognition_number: string | null;
  detail: string;
};

export type AccountReconciliation = {
  account_id: string;
  account_code: string;
  account_name: string;
  recognition_controlled: boolean;
  subledger_balance: string;
  gl_balance: string;
  difference: string;
  explained: string;
  unexplained: string;
  contract_count: number;
  exceptions: ReconciliationException[];
};

export type ReconciliationReport = { as_of: string; accounts: AccountReconciliation[]; totals: Record<string, string> };

export type IntegrityIssue = {
  recognition_contract_id: string;
  recognition_number: string;
  check: string;
  expected: string;
  actual: string;
  detail: string;
};

export type JobRun = {
  id: string;
  job_name: string;
  company_id: string | null;
  status: string;
  processed_count: number;
  failed_count: number;
  summary: Record<string, unknown> | null;
  error_message: string | null;
  started_at: string;
  finished_at: string | null;
};

export const RECOGNITION_JOBS = ['UPDATE_DUE_LINES', 'GENERATE_DRAFT_JVS', 'AUTO_POST', 'EXPIRY_NOTIFICATIONS', 'INTEGRITY_CHECK'] as const;

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

type Params = Record<string, string | number | boolean | undefined | null>;

function clean(params?: Params): Record<string, string> {
  const out: Record<string, string> = {};
  if (!params) return out;
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') out[k] = String(v);
  }
  return out;
}

const BASE = '/finance/recognition';

class RecognitionService {
  // contracts
  async listContracts(params?: Params) {
    return unwrap<Paged<ContractListRow>>(await api.get(`${BASE}/contracts`, { params: clean(params) }));
  }
  async getContract(id: string) {
    return unwrap<ContractDetail>(await api.get(`${BASE}/contracts/${id}`));
  }
  async previewSchedule(input: ContractInput) {
    return unwrap<SchedulePreview>(await api.post(`${BASE}/contracts/preview-schedule`, input));
  }
  async createContract(input: ContractInput) {
    return unwrap<ContractDetail>(await api.post(`${BASE}/contracts`, input));
  }
  async updateContract(id: string, input: ContractInput) {
    return unwrap<ContractDetail>(await api.put(`${BASE}/contracts/${id}`, input));
  }
  async submitContract(id: string) {
    return unwrap<ContractDetail>(await api.post(`${BASE}/contracts/${id}/submit`, {}));
  }
  async approveContract(id: string, remarks?: string) {
    return unwrap<ContractDetail>(await api.post(`${BASE}/contracts/${id}/approve`, { remarks }));
  }
  async rejectContract(id: string, payload: RejectionPayload | { reason: string }) {
    return unwrap<ContractDetail>(await api.post(`${BASE}/contracts/${id}/reject`, payload));
  }
  async activateContract(id: string) {
    return unwrap<ContractDetail>(await api.post(`${BASE}/contracts/${id}/activate`, {}));
  }
  async cancelContract(id: string, reason: string) {
    return unwrap<ContractDetail>(await api.post(`${BASE}/contracts/${id}/cancel`, { reason }));
  }
  async closeContract(id: string, reason?: string) {
    return unwrap<ContractDetail>(await api.post(`${BASE}/contracts/${id}/close`, { reason }));
  }
  async contractSchedule(id: string, params?: Params) {
    return unwrap<Paged<ScheduleLineRow> & { totals: { scheduled: string; posted: string; open_amount: string } }>(
      await api.get(`${BASE}/contracts/${id}/schedule`, { params: clean(params) })
    );
  }
  async contractEvents(id: string) {
    return unwrap<ContractEvent[]>(await api.get(`${BASE}/contracts/${id}/events`));
  }
  async contractJournals(id: string) {
    return unwrap<ContractJournal[]>(await api.get(`${BASE}/contracts/${id}/journals`));
  }
  async contractMovements(id: string) {
    return unwrap<ContractMovement[]>(await api.get(`${BASE}/contracts/${id}/movements`));
  }
  async contractAdjustments(id: string) {
    return unwrap<Adjustment[]>(await api.get(`${BASE}/contracts/${id}/adjustments`));
  }

  // adjustments
  async createAdjustment(contractId: string, input: AdjustmentInput) {
    return unwrap<Adjustment>(await api.post(`${BASE}/contracts/${contractId}/adjustments`, input));
  }
  async previewAddition(contractId: string, amount: string | number, exchangeRate?: string | number | null) {
    return unwrap<{ base_amount: string; base_currency: string; transaction_currency: string }>(
      await api.post(`${BASE}/contracts/${contractId}/adjustments/preview-addition`, { amount, exchange_rate: exchangeRate })
    );
  }
  async pendingAdjustments() {
    return unwrap<Adjustment[]>(await api.get(`${BASE}/adjustments/pending`));
  }
  async getAdjustment(id: string) {
    return unwrap<Adjustment>(await api.get(`${BASE}/adjustments/${id}`));
  }
  async approveAdjustment(id: string, remarks?: string) {
    return unwrap<Adjustment>(await api.post(`${BASE}/adjustments/${id}/approve`, { remarks }));
  }
  async rejectAdjustment(id: string, payload: RejectionPayload | { reason: string }) {
    return unwrap<Adjustment>(await api.post(`${BASE}/adjustments/${id}/reject`, payload));
  }
  async retryAdjustment(id: string) {
    return unwrap<Adjustment>(await api.post(`${BASE}/adjustments/${id}/retry`, {}));
  }

  // lines / drafts
  async blockedLineAction(lineId: string, action: string, reason: string, postNow = false) {
    return unwrap<{ message?: string; posted?: boolean }>(
      await api.post(`${BASE}/lines/${lineId}/blocked-action`, { action, reason, post_now: postNow })
    );
  }
  async manualPost(lineIds: string[], postingAction?: 'POST' | 'DRAFT') {
    return unwrap<LineOutcome[]>(await api.post(`${BASE}/lines/post`, { line_ids: lineIds, posting_action: postingAction }));
  }
  async postDraft(postingId: string) {
    return unwrap<{ journalVoucherId: string; voucherNumber: string }>(await api.post(`${BASE}/postings/${postingId}/post`, {}));
  }
  async discardDraft(postingId: string, reason: string) {
    return unwrap<null>(await api.post(`${BASE}/postings/${postingId}/discard`, { reason }));
  }

  // runs
  async previewRun(filters: RunFilters) {
    return unwrap<RunPreview>(await api.post(`${BASE}/runs/preview`, filters));
  }
  async createRun(input: RunCreateInput) {
    return unwrap<RecognitionRun>(await api.post(`${BASE}/runs`, input));
  }
  async processRun(runId: string) {
    return unwrap<RecognitionRun>(await api.post(`${BASE}/runs/${runId}/process`, {}));
  }
  async discardRun(runId: string, reason: string) {
    return unwrap<RecognitionRun>(await api.post(`${BASE}/runs/${runId}/discard`, { reason }));
  }
  async listRuns(params?: Params) {
    return unwrap<Paged<RecognitionRun>>(await api.get(`${BASE}/runs`, { params: clean(params) }));
  }
  async getRun(runId: string, params?: Params) {
    return unwrap<RunDetail>(await api.get(`${BASE}/runs/${runId}`, { params: clean(params) }));
  }

  // settings
  async getPolicy() {
    return unwrap<RecognitionPolicy>(await api.get(`${BASE}/policy`));
  }
  async updatePolicy(input: Partial<RecognitionPolicy>) {
    return unwrap<RecognitionPolicy>(await api.put(`${BASE}/policy`, input));
  }
  async listCategories(includeInactive = false) {
    return unwrap<RecognitionCategory[]>(await api.get(`${BASE}/categories`, { params: includeInactive ? { include_inactive: 'true' } : {} }));
  }
  async createCategory(input: Partial<RecognitionCategory>) {
    return unwrap<RecognitionCategory>(await api.post(`${BASE}/categories`, input));
  }
  async updateCategory(id: string, input: Partial<RecognitionCategory>) {
    return unwrap<RecognitionCategory>(await api.put(`${BASE}/categories/${id}`, input));
  }
  async listControlledAccounts() {
    return unwrap<ControlledAccount[]>(await api.get(`${BASE}/controlled-accounts`));
  }
  async setControlledAccount(accountId: string, controlled: boolean) {
    return unwrap<ControlledAccount>(await api.put(`${BASE}/controlled-accounts/${accountId}`, { controlled }));
  }

  // dashboard / reports
  async workspace(asOf?: string) {
    return unwrap<WorkspaceData>(await api.get(`${BASE}/workspace`, { params: clean({ as_of: asOf }) }));
  }
  async registerReport(params?: Params) {
    return unwrap<RegisterReport>(await api.get(`${BASE}/reports/register`, { params: clean(params) }));
  }
  async scheduleReport(params?: Params) {
    return unwrap<ScheduleReport>(await api.get(`${BASE}/reports/schedule`, { params: clean(params) }));
  }
  async movementReport(params?: Params) {
    return unwrap<MovementReport>(await api.get(`${BASE}/reports/movement`, { params: clean(params) }));
  }
  async forecastReport(params?: Params) {
    return unwrap<ForecastReport>(await api.get(`${BASE}/reports/forecast`, { params: clean(params) }));
  }
  async reconciliation(params?: Params) {
    return unwrap<ReconciliationReport>(await api.get(`${BASE}/reconciliation`, { params: clean(params) }));
  }
  async integrity() {
    return unwrap<{ checked: number; issues: IntegrityIssue[] }>(await api.get(`${BASE}/reconciliation/integrity`));
  }

  // jobs
  async jobRuns(limit = 50) {
    return unwrap<JobRun[]>(await api.get(`${BASE}/jobs/runs`, { params: { limit } }));
  }
  async runJob(job: string) {
    return unwrap<{ job: string; companies: number; processed: number; failed: number }>(await api.post(`${BASE}/jobs/${job}/run`, {}));
  }

  // drill-through
  async resolve(kind: 'line' | 'revision' | 'posting', id: string) {
    return unwrap<{ contract_id: string | null; run_id: string | null }>(await api.get(`${BASE}/resolve/${kind}/${id}`));
  }
}

export const recognitionService = new RecognitionService();
export default recognitionService;
